import React, { useEffect, useState } from "react";
import { getWithTokenAPI } from "../../utils/Api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  CircularProgress,
  Box,
  Typography,
  Pagination,
  IconButton,
} from "@mui/material";
import { ReceiptLong as ReceiptLongIcon } from "@mui/icons-material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from "react-router-dom";
import GroupIcon from "@mui/icons-material/Group";

const SubscriptionManagementPage = () => {
  const [subscriptionData, setSubscriptionData] = useState([]);
  const [filteredSubscriptions, setFilteredSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [subscriptionsPerPage] = useState(10);

  // Colors
  const primaryGreen = "#DC3545";
  const lightGreen = "#ffe5e5";
  const secondaryTextColor = "rgba(0, 0, 0, 0.6)";

  // Fetch all subscription data
  const fetchSubscriptions = async () => {
    const token = localStorage.getItem("token");
    setLoading(true);
    try {
      const res = await getWithTokenAPI(token, "/subscription/find-all");

      if (res.statusCode === 200) {
        const allSubscriptions = res.data.subscriber || [];
        setSubscriptionData(allSubscriptions);
        
        // Filter to only show Paid subscriptions
        const paidSubscriptions = allSubscriptions.filter(
          sub => sub.paid_status === "Paid"
        );
        setFilteredSubscriptions(paidSubscriptions);
      } else {
        toast.error(res.message || "Failed to load subscriptions.");
        setSubscriptionData([]);
        setFilteredSubscriptions([]);
      }
    } catch (error) {
      console.error("Fetch subscriptions error:", error);
      toast.error("Failed to load subscriptions due to a network error.");
      setSubscriptionData([]);
      setFilteredSubscriptions([]);
    } finally {
      setLoading(false);
    }
  };

  // Pagination logic
  const indexOfLastSubscription = currentPage * subscriptionsPerPage;
  const indexOfFirstSubscription = indexOfLastSubscription - subscriptionsPerPage;
  const currentSubscriptions = filteredSubscriptions.slice(
    indexOfFirstSubscription,
    indexOfLastSubscription
  );
  const totalPages = Math.ceil(filteredSubscriptions.length / subscriptionsPerPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Handle click on user name to navigate to user details
  const handleUserNameClick = (userId) => {
    if (userId) {
      navigate(`/backoffice/reviewuser/${userId}`);
    }
  };

  // Initial data fetch on component mount
  useEffect(() => {
    fetchSubscriptions();
  }, []);

  return (
    <Box sx={{ p: 3, width: "100%" }}>
      <ToastContainer position="top-center" autoClose={1500} />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
          p: 2,
          backgroundColor: lightGreen,
          borderRadius: 2,
          borderLeft: `5px solid ${primaryGreen}`,
        }}
      >
        <ReceiptLongIcon sx={{ color: primaryGreen, fontSize: 50, mr: 2 }} />
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{ color: primaryGreen, fontWeight: "bold" }}
          >
            Subscription Management
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            Manage your paid user subscriptions
          </Typography>
        </Box>
      </Box>

      {/* Main Content - Updated Table Design */}
      <div className="table-container border rounded">
        <div className="table-responsive">
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
              }}
            >
              <CircularProgress style={{ color: "#DC3545" }} />
              <Typography sx={{ ml: 2, color: "#DC3545" }}>Loading subscriptions...</Typography>
            </Box>
          ) : (
            <table className="table text-center m-0 resource-head">
              <thead
                className="sticky-header fs-6"
                style={{ backgroundColor: lightGreen }}
              >
                <tr>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor, textAlign: 'left' }}
                  >
                    S.No
                  </th>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor, textAlign: 'left' }}
                  >
                    User
                  </th>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor, textAlign: 'left' }}
                  >
                    Amount(Ksh)
                  </th>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor, textAlign: 'left' }}
                  >
                    Tax Amount(Ksh)
                  </th>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor, textAlign: 'left' }}
                  >
                    Total Amount(Ksh)
                  </th>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor, textAlign: 'left' }}
                  >
                    Paid Status
                  </th>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor, textAlign: 'left' }}
                  >
                    Start Date
                  </th>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor, textAlign: 'left' }}
                  >
                    End Date
                  </th>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor, textAlign: 'left' }}
                  >
                    Remaining Days
                  </th>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor, textAlign: 'left' }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentSubscriptions.length > 0 ? (
                  currentSubscriptions.map((item, index) => (
                    <tr key={item._id}>
                      <td style={{textAlign: 'left'}}>{indexOfFirstSubscription + index + 1}</td>
                      <td
                        onClick={() => handleUserNameClick(item.user_id?._id)}
                        style={{
                          fontWeight: "bold",
                          color: primaryGreen,
                          cursor: "pointer",
                          textDecoration: "none",
                          textAlign: 'left'
                        }}
                        onMouseEnter={(e) =>
                          (e.target.style.textDecoration = "underline")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.textDecoration = "none")
                        }
                      >
                        {item.user_id?.name || "N/A"}
                      </td>
                      <td style={{textAlign: 'left'}}>{item.amount?.toFixed(2) || "0.00"}</td>
                      <td style={{textAlign: 'left'}} >{item.tax_amount?.toFixed(2) || "0.00"}</td>
                      <td style={{textAlign: 'left'}}>{item.total_amount?.toFixed(2) || "0.00"}</td>
                      <td style={{textAlign: 'left'}}>{item.paid_status || "N/A"}</td>
                      <td style={{textAlign: 'left'}}>
                        {item.start_date
                          ? new Date(item.start_date).toLocaleDateString()
                          : "N/A"}
                      </td>
                      <td style={{textAlign: 'left'}}>
                        {item.end_date
                          ? new Date(item.end_date).toLocaleDateString()
                          : "N/A"}
                      </td>
                      <td style={{textAlign: 'left'}}>
                        {item.remaining_days !== undefined
                          ? `${item.remaining_days} days`
                          : "N/A"}
                      </td>
                      <td style={{textAlign: 'left'}}>
                        <IconButton
                          color="info"
                          onClick={() => handleUserNameClick(item.user_id?._id)}
                          title="View User Details"
                          sx={{ color: primaryGreen }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="text-center">
                      <GroupIcon
                        sx={{ fontSize: 60, color: "text.secondary" }}
                      />
                      <Typography variant="h6" sx={{ color: "text.secondary" }}>
                        No paid subscriptions found.
                      </Typography>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "end",
            mt: 3,
            pb: 2,
          }}
        >
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            sx={{
              "& .MuiPaginationItem-root": {
                color: primaryGreen,
              },
              "& .MuiPaginationItem-root.Mui-selected": {
                backgroundColor: primaryGreen,
                color: "white",
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default SubscriptionManagementPage;